/**
 * Request Integration Modal
 *
 * Dedicated modal for users to request a new third-party integration
 * (Linear, Notion, ServiceNow, etc.). Submits to the generic /contact
 * endpoint with a structured subject + body so the team can triage it
 * as an integration request rather than a sales inquiry.
 */
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Send,
  Plug,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Building,
} from 'lucide-react'

interface RequestIntegrationModalProps {
  isOpen: boolean
  onClose: () => void
  prefillName?: string
  prefillEmail?: string
  prefillCompany?: string
}

type Priority = 'exploring' | 'would-use' | 'blocker'

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'exploring', label: 'Exploring' },
  { value: 'would-use', label: 'Would use' },
  { value: 'blocker', label: 'Blocker' },
]

export function RequestIntegrationModal({
  isOpen,
  onClose,
  prefillName,
  prefillEmail,
  prefillCompany,
}: RequestIntegrationModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    integrationName: '',
    useCase: '',
    priority: 'would-use' as Priority,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null)

  // Prefill user identity when the modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        name: prefillName || prev.name,
        email: prefillEmail || prev.email,
        company: prefillCompany || prev.company,
      }))
      setSubmitResult(null)
    }
  }, [isOpen, prefillName, prefillEmail, prefillCompany])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitResult(null)

    const priorityLabel =
      PRIORITY_OPTIONS.find((p) => p.value === formData.priority)?.label || formData.priority

    // Compose a clearly-labelled integration request body. The /contact
    // endpoint is generic, so we encode the structure in the message.
    const message = [
      `Integration requested: ${formData.integrationName}`,
      `Priority: ${priorityLabel}`,
      '',
      'Use case:',
      formData.useCase,
    ].join('\n')

    try {
      const response = await fetch(
        `${import.meta.env.VITE_IDENTITY_API_URL || 'https://api.ytest.ai'}/api/v1/contact`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            company: formData.company || undefined,
            subject: `Integration Request: ${formData.integrationName}`,
            message,
          }),
        },
      )

      if (response.ok) {
        setSubmitResult({
          success: true,
          message: 'Thanks! We\'ll review your request and follow up soon.',
        })
        setFormData((prev) => ({
          ...prev,
          integrationName: '',
          useCase: '',
          priority: 'would-use',
        }))
      } else {
        const error = await response.json().catch(() => ({}))
        setSubmitResult({
          success: false,
          message: error.detail || 'Failed to send request. Please try again.',
        })
      }
    } catch (err) {
      setSubmitResult({ success: false, message: 'Failed to send request. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop (non-dismissive — close only via the X button) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg bg-card rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-success to-success px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-card/20 rounded-lg">
                    <Plug className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Request a new integration</h2>
                    <p className="text-white text-sm mt-0.5">
                      Tell us what you'd like to connect — we prioritize by demand.
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-card/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
              {submitResult && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl flex items-center gap-3 ${
                    submitResult.success
                      ? 'bg-success-soft text-success-foreground border border-success/30'
                      : 'bg-destructive-soft text-destructive-foreground border border-destructive/30'
                  }`}
                >
                  {submitResult.success ? (
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span className="text-sm">{submitResult.message}</span>
                </motion.div>
              )}

              {/* Integration name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Which integration? *
                </label>
                <div className="relative">
                  <Plug className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                  <input
                    type="text"
                    name="integrationName"
                    value={formData.integrationName}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Linear, Notion, ServiceNow, TestRail"
                    className="w-full pl-10 pr-4 py-2.5 border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success"
                  />
                </div>
              </div>

              {/* Use case */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  What would you use it for? *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-subtle-foreground" />
                  <textarea
                    name="useCase"
                    value={formData.useCase}
                    onChange={handleChange}
                    required
                    rows={3}
                    placeholder="e.g. File bugs from failed test runs into Linear, or query our test data warehouse during Agent execution."
                    className="w-full pl-10 pr-4 py-2.5 border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success resize-none"
                  />
                </div>
              </div>

              {/* Priority — horizontal segmented control */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  How important is this? *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {PRIORITY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center justify-center px-3 py-2 text-sm font-medium border rounded-lg cursor-pointer transition text-center ${
                        formData.priority === option.value
                          ? 'border-success bg-success-soft text-success-foreground'
                          : 'border-strong text-foreground hover:border'
                      }`}
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={option.value}
                        checked={formData.priority === option.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Contact info (collapsed when prefilled, expanded when not) */}
              <div className="pt-2 border-t border-strong">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                  Your details
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        readOnly={!!prefillName}
                        placeholder="Name"
                        className={`w-full pl-10 pr-4 py-2 text-sm border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success ${
                          prefillName ? 'bg-foreground/[0.04] cursor-not-allowed' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        readOnly={!!prefillEmail}
                        placeholder="Email"
                        className={`w-full pl-10 pr-4 py-2 text-sm border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success ${
                          prefillEmail ? 'bg-foreground/[0.04] cursor-not-allowed' : ''
                        }`}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle-foreground" />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      readOnly={!!prefillCompany}
                      placeholder="Company (optional)"
                      className={`w-full pl-10 pr-4 py-2 text-sm border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success ${
                        prefillCompany ? 'bg-foreground/[0.04] cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-success to-success text-white font-semibold rounded-lg hover:from-success hover:to-success disabled:bg-none disabled:bg-disabled disabled:text-disabled-foreground disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit request
                  </>
                )}
              </button>

              <p className="text-xs text-muted-foreground text-center">
                We review every request. Popular integrations ship faster.
              </p>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default RequestIntegrationModal
